if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      lastChangedIndex = -1;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');

        // Delegated: keeps working after variant-selects is re-rendered
        this.addEventListener('click', (event) => {
          if (!event.target.closest('[data-variant-clear]')) return;
          event.preventDefault();
          this.clearVariantSelection();
        });
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initVariantSelectionState();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        this.abortController?.abort();
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push(() => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      /* ------------------------------------------------------------------
       * QUANTITY
       * ---------------------------------------------------------------- */

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      setQuantityBoundries() {
        if (!this.quantityInput) return;

        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput || !this.quantityForm) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!quantityFormUpdated) return;

        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (const selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (const attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                const currentHiddenLabel = document.getElementById(updatedAriaLabelledBy);
                const updatedHiddenLabel = html.getElementById(updatedAriaLabelledBy);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      /* ------------------------------------------------------------------
       * SELECTION HELPERS
       * ---------------------------------------------------------------- */

      normalizeOptionValue(value) {
        return String(value ?? '')
          .trim()
          .replace(/\s*\+\s*/g, '+')
          .replace(/\s+/g, ' ')
          .toLowerCase();
      }

      getGroups(root = this.variantSelectors) {
        return root ? Array.from(root.querySelectorAll('fieldset, select')) : [];
      }

      getCurrentSelections(root = this.variantSelectors) {
        return this.getGroups(root).map((group) => {
          if (group.tagName === 'SELECT') return group.value || null;
          return group.querySelector('input[type="radio"]:checked')?.value || null;
        });
      }

      hasCompleteSelection() {
        const selections = this.getCurrentSelections();
        return selections.length > 0 && selections.every(Boolean);
      }

      getSelectedOptionValueIds() {
        return this.getGroups()
          .map((group) => {
            if (group.tagName === 'SELECT') return group.selectedOptions[0]?.dataset.optionValueId;
            return group.querySelector('input[type="radio"]:checked')?.dataset.optionValueId;
          })
          .filter(Boolean);
      }

      // Writes selection into DOM *attributes* too, because viewTransition clones via outerHTML
      applySelections(root, selections) {
        if (!root) return;

        this.getGroups(root).forEach((group, index) => {
          const value = selections[index] || null;

          if (group.tagName === 'SELECT') {
            const options = Array.from(group.options);
            const match =
              (value && options.find((option) => option.value === value)) ||
              group.querySelector('option[data-placeholder]') ||
              options[0];

            options.forEach((option) => {
              option.removeAttribute('selected');
              option.selected = false;
            });

            if (match) {
              match.setAttribute('selected', 'selected');
              match.selected = true;
            }
            return;
          }

          group.querySelectorAll('input[type="radio"]').forEach((input) => {
            const isChecked = value !== null && input.value === value;
            input.checked = isChecked;
            input.toggleAttribute('checked', isChecked);
          });

          const label = group.querySelector('[data-selected-value]');
          if (label) label.textContent = value || '';
        });

        const clearButton = root.querySelector('[data-variant-clear]');
        if (clearButton) clearButton.hidden = !selections.some(Boolean);
      }

      syncSelectedLabels() {
        this.getGroups().forEach((group) => {
          if (group.tagName === 'SELECT') return;
          const label = group.querySelector('[data-selected-value]');
          if (!label) return;
          label.textContent = group.querySelector('input[type="radio"]:checked')?.value || '';
        });
      }

      updateClearButton() {
        const clearButton = this.querySelector('[data-variant-clear]');
        if (clearButton) clearButton.hidden = !this.getCurrentSelections().some(Boolean);
      }

      /* ------------------------------------------------------------------
       * VARIANT DATA
       * ---------------------------------------------------------------- */

      getVariantData() {
        try {
          const el =
            this.querySelector(`#VariantMedia-${this.dataset.section}`) ||
            this.querySelector(`#VariantMediaMap-${this.dataset.section}`);
          if (!el) return null;
          const data = JSON.parse(el.textContent || '[]');
          return Array.isArray(data) ? data : null;
        } catch (error) {
          console.error('Variant data parse error:', error);
          return null;
        }
      }

      // Liquid only exposes the first 250 variants. If we hit that cap, local data is incomplete.
      isVariantDataComplete() {
        const variants = this.getVariantData();
        return Array.isArray(variants) && variants.length < 250;
      }

      getLocalMatch() {
        const variants = this.getVariantData();
        if (!variants) return null;
        const selections = this.getCurrentSelections();
        if (!selections.length || !selections.every(Boolean)) return null;

        return (
          variants.find(
            (variant) =>
              Array.isArray(variant.o) &&
              variant.o.length === selections.length &&
              variant.o.every(
                (value, i) => this.normalizeOptionValue(value) === this.normalizeOptionValue(selections[i])
              )
          ) || null
        );
      }

      // Swatch groups (Gold colour, Bracelet colour) decide the image. Size does not.
      getImageOptionIndexes() {
        const indexes = [];
        this.getGroups().forEach((group, i) => {
          if (group.dataset.pickerType === 'swatch') indexes.push(i);
        });
        return indexes;
      }

      getFallbackMediaId() {
        const variants = this.getVariantData();
        if (!variants) return null;

        const selections = this.getCurrentSelections();
        if (!selections.some(Boolean)) return null;

        const imageIdx = this.getImageOptionIndexes();
        let best = null;
        let bestScore = 0;

        variants.forEach((variant) => {
          if (!variant.m || !Array.isArray(variant.o)) return;

          let score = 0;
          let imageHit = false;

          selections.forEach((selected, i) => {
            if (!selected) return;
            if (this.normalizeOptionValue(variant.o[i]) !== this.normalizeOptionValue(selected)) return;

            const isImg = imageIdx.includes(i);
            score += isImg ? 100 : 1;
            if (i === this.lastChangedIndex) score += 10;
            if (isImg) imageHit = true;
          });

          if (imageIdx.length && !imageHit) return;
          if (variant.a) score += 0.5;

          if (score > bestScore) {
            best = variant;
            bestScore = score;
          }
        });

        return best?.m || null;
      }

      // Greys out values that do not exist for the other selections
      updatePartialAvailability() {
        const variants = this.getVariantData();
        const groups = this.getGroups();
        if (!variants || !groups.length) return;

        if (!this.isVariantDataComplete()) {
          groups.forEach((group) => {
            if (group.tagName === 'SELECT') {
              Array.from(group.options).forEach((option) => {
                if (option.value) option.disabled = false;
              });
            } else {
              group.querySelectorAll('input[type="radio"]').forEach((input) => input.classList.remove('disabled'));
            }
          });
          return;
        }

        const selections = this.getCurrentSelections();
        const norm = (v) => this.normalizeOptionValue(v);

        const exists = (index, value, requireAvailable) =>
          variants.some((variant) => {
            if (!Array.isArray(variant.o)) return false;
            if (requireAvailable && !variant.a) return false;
            if (norm(variant.o[index]) !== norm(value)) return false;
            return selections.every((sel, i) => i === index || !sel || norm(variant.o[i]) === norm(sel));
          });

        groups.forEach((group, index) => {
          if (group.tagName === 'SELECT') {
            Array.from(group.options).forEach((option) => {
              if (!option.value) return;
              option.disabled = !exists(index, option.value, false);
            });
            return;
          }

          group.querySelectorAll('input[type="radio"]').forEach((input) => {
            input.classList.toggle('disabled', !exists(index, input.value, true));
          });
        });
      }

      /* ------------------------------------------------------------------
       * INCOMPLETE / CLEAR
       * ---------------------------------------------------------------- */

      initVariantSelectionState() {
        if (!this.variantSelectors) return;

        this.syncSelectedLabels();
        this.updateClearButton();

        if (this.hasCompleteSelection()) {
          this.updatePartialAvailability();
          return;
        }

        this.updatePartialAvailability();
        this.setIncompleteState();
      }

      setIncompleteState() {
        this.classList.add('variant-incomplete');
        this.updateVariantInputs(null);

        customElements.whenDefined('product-form').then(() => {
          if (!this.classList.contains('variant-incomplete')) return;
          this.productForm?.toggleSubmitButton(true, window.variantStrings?.addToCart);
          this.productForm?.handleErrorMessage?.();
        });
      }

      clearVariantSelection() {
        const root = this.variantSelectors;
        if (!root) return;

        this.abortController?.abort();
        this.pendingRequestUrl = null;
        this.lastChangedIndex = -1;

        this.applySelections(root, []);
        this.updatePartialAvailability();
        this.setIncompleteState();
        this.updateURL(this.dataset.url);

        root.querySelector('fieldset input[type="radio"], select')?.focus();
      }

      /* ------------------------------------------------------------------
       * OPTION CHANGE
       * ---------------------------------------------------------------- */

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        const groups = this.getGroups();
        this.lastChangedIndex = groups.findIndex((g) => g === event.target || g.contains(event.target));

        this.syncSelectedLabels();
        this.updateClearButton();
        this.updatePartialAvailability();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;

        // Not all options picked yet: no fetch, just preview matching image
        if (!shouldSwapProduct && !this.hasCompleteSelection()) {
          this.abortController?.abort();
          this.pendingRequestUrl = null;
          this.setIncompleteState();
          this.showPartialSelectionMedia();
          return;
        }

        this.classList.remove('variant-incomplete');
        this.resetProductFormState();
        this.instantVariantSwap();
        this.updatePriceFromTemplate();

        this.pendingRequestUrl = productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        const ownIds = this.getSelectedOptionValueIds();
        const optionValues = ownIds.length === groups.length ? ownIds : (selectedOptionValues || []).filter(Boolean);

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, optionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        this.productForm?.toggleSubmitButton(true);
        this.productForm?.handleErrorMessage();
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];
        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);
        if (optionValues.length) params.push(`option_values=${optionValues.join(',')}`);
        return `${url}${url.includes('?') ? '&' : '?'}${params.join('&')}`;
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`Variant request failed: ${response.status}`);
            return response.text();
          })
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') return;
            console.error(error);
            this.variantSelectors?.rejectPendingSelectPromise?.(error);
          });
      }

      /* ------------------------------------------------------------------
       * SWAP / UPDATE
       * ---------------------------------------------------------------- */

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(parent?.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        return this.parseJsonScript(this.getVariantSelects(productInfoNode), '[data-selected-variant]');
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const sourceProductInfo = html.querySelector(selector);
          if (!sourceProductInfo) return;

          const currentSelections = this.getCurrentSelections();
          const sourceSelects = this.getVariantSelects(sourceProductInfo);
          if (sourceSelects) this.applySelections(sourceSelects, currentSelections);

          const variant = this.getSelectedVariant(sourceProductInfo);
          this.variantSelectors?.resolvePendingSelectPromise?.(variant, sourceSelects);
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            const currentTitle = document.querySelector('head title');
            const newTitle = html.querySelector('head title');
            if (currentTitle && newTitle) currentTitle.innerHTML = newTitle.innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      updateOptionValues(html) {
        const incoming = html.querySelector('variant-selects');
        const current = this.variantSelectors;
        if (!incoming || !current) return;

        // Keep what the customer picked (server markup may be un-preselected)
        this.applySelections(incoming, this.getCurrentSelections(current));
        HTMLUpdateUtility.viewTransition(current, incoming, this.preProcessHtmlCallbacks);

        this.updatePartialAvailability();
        this.updateClearButton();
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const safe = (fn) => {
            try {
              fn();
            } catch (e) {
              console.error(e);
            }
          };

          const sourceVariantSelects = this.getVariantSelects(html);
          const serverVariant = this.getSelectedVariant(html);
          const local = this.getLocalMatch();
          const hasLocalData = this.isVariantDataComplete();

          // Our own data is the source of truth for which variant exists
          let variant = serverVariant;
          if (hasLocalData) {
            if (!local) {
              variant = null;
            } else if (!serverVariant || String(serverVariant.id) !== String(local.id)) {
              variant = {
                id: local.id,
                available: Boolean(local.a),
                featured_media: local.m ? { id: local.m } : null,
              };
            }
          }

          // Resolve before variant-selects is replaced
          const mediaId = variant?.featured_media?.id || this.getFallbackMediaId();

          this.variantSelectors?.resolvePendingSelectPromise?.(variant, sourceVariantSelects);
          safe(() => this.pickupAvailability?.update(variant));
          safe(() => this.updateOptionValues(html));
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (mediaId) safe(() => this.updateMedia(html, mediaId));

          if (!variant) {
            this.setUnavailable();
            return;
          }

          const updateSourceFromDestination = (id, shouldHide = () => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText.trim() === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          safe(() => {
            this.updateQuantityRules(this.sectionId, html);
            this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
            this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');
          });

          this.productForm?.toggleSubmitButton(!variant.available, window.variantStrings.soldOut);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: { sectionId: this.sectionId, html, variant },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      /* ------------------------------------------------------------------
       * INSTANT SWAP + MEDIA
       * ---------------------------------------------------------------- */

      instantVariantSwap() {
        try {
          const match = this.getLocalMatch();

          if (match) {
            this.updateVariantInputs(match.id);
            this.productForm?.toggleSubmitButton(!match.a, window.variantStrings.soldOut);
            this.updateURL(this.dataset.url, match.id);
          }

          this.activateMedia((match && match.m) || this.getFallbackMediaId());
        } catch (e) {
          console.error(e);
        }
      }

      showPartialSelectionMedia() {
        try {
          this.activateMedia(this.getFallbackMediaId());
        } catch (e) {
          console.error(e);
        }
      }

      activateMedia(mediaId) {
          if (!mediaId) return;
          const gallery = this.querySelector('media-gallery');
          if (!gallery) return;

          const slideId = `${this.dataset.section}-${mediaId}`;
          const slide = gallery.querySelector(`li[data-media-id="${slideId}"]`);
          if (!slide) return;

          const img = slide.querySelector('img');
          if (img) img.loading = 'eager';

          // Quick view: apna slider handle karega, DOM reorder nahi
          if (this.dataset.originalSection) {
            this.dispatchEvent(new CustomEvent('quickadd:media', { detail: { slide } }));
            return;
          }

          gallery.setActiveMedia?.(slideId, true);
        }

      updatePriceFromTemplate() {
        try {
          const selections = this.getCurrentSelections();
          if (!selections.length || !selections.every(Boolean)) return;

          const key = selections.join('|');
          const template = Array.from(this.querySelectorAll(`#PriceTemplates-${this.dataset.section} template`)).find(
            (item) => item.dataset.options === key
          );
          const priceElement = this.querySelector(`#price-${this.dataset.section}`);
          if (!template || !priceElement) return;

          priceElement.innerHTML = template.innerHTML;
          priceElement.classList.remove('hidden');
        } catch (e) {
          console.error(e);
        }
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector('media-gallery ul');

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover') && typeof enableZoomOnHover === 'function') enableZoomOnHover(2);
          const items = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const set = new Set(items.map((item) => item.dataset.mediaId));
          const map = new Map(items.map((item, index) => [item.dataset.mediaId, { item, index }]));
          return [items, set, map];
        };

       if (mediaGallerySource && mediaGalleryDestination && !this.dataset.originalSection) {
          let [sourceItems, sourceSet, sourceMap] = refreshSourceData();
          const destinationItems = Array.from(mediaGalleryDestination.querySelectorAll('li[data-media-id]'));
          const destinationSet = new Set(destinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          for (let i = destinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(destinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(destinationItems[i]);
              shouldRefresh = true;
            }
          }

          for (let i = 0; i < sourceItems.length; i++) {
            if (!destinationSet.has(sourceItems[i].dataset.mediaId)) {
              sourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          if (shouldRefresh) [sourceItems, sourceSet, sourceMap] = refreshSourceData();

          destinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);
            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );
              [sourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        this.activateMedia(variantFeaturedMediaId);

        const modalContent = this.productModal?.querySelector('.product-media-modal__content');
        const newModalContent = html.querySelector('product-modal .product-media-modal__content');
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      /* ------------------------------------------------------------------
       * GETTERS
       * ---------------------------------------------------------------- */

      get productForm() {
        return this.querySelector('product-form');
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector('pickup-availability');
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

document.addEventListener(
  'mouseover',
  (e) => {
    const input = e.target.closest('fieldset input[type="radio"], fieldset label');
    if (!input) return;
    document.querySelectorAll('.product__media-item img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });
  },
  { once: true }
);